import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import FontFamily from "@tiptap/extension-font-family";
import Heading from "@tiptap/extension-heading";
import TextAlign from "@tiptap/extension-text-align";
import { Button } from "@mui/material";
import { Box } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import Link from "@tiptap/extension-link";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import { FontSize } from "@tiptap/extension-text-style";
import { WordToolbar } from "../WordToolbar";
import { useEffect, useState } from "react";
import { InternalAIChatBox } from "../InternalAIChatBox";
import { Page } from "../../../services/documentBuilderService";
import "./Editor.css";
import { SelectionData } from "../DocumentBuilderType";
import { PageAIChatBox } from "../PageAIChatBox";
import { LayoutContainer } from "../LayoutContainer";
import { RemovableImage } from "../RemovableImage";
import { DocumentTheme } from "../DocumentTheme";

interface EditorProps {
  content: string;
  setDocumentTheme: (theme: string) => void;
  documentTheme: DocumentTheme;
}

export function Editor({
  content,
  setDocumentTheme,
  documentTheme,
}: EditorProps) {
  const editor = useEditor({
    extensions: [
      LayoutContainer,
      StarterKit.configure({
        paragraph: {
          HTMLAttributes: {},
        },
        heading: {
          HTMLAttributes: {},
        },
      }),

      Underline,
      TextStyle,
      Color,
      OrderedList,
      ListItem,
      Highlight,
      FontFamily,
      Heading,
      FontSize,
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      RemovableImage,
      Link.configure({
        openOnClick: true,
      }),
    ],
    content,
  });

  const [selection, setSelection] = useState<SelectionData | null>(null);
  const [showAI, setShowAI] = useState(false);
  const [showPageAI, setShowPageAI] = useState(false);

  const [aiBtnPos, setAiBtnPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  /* ───────── TRACK TEXT SELECTION ───────── */
  useEffect(() => {
    if (!editor) return;

    const updateSelection = () => {
      const { from, to } = editor.state.selection;
      const text = editor.state.doc.textBetween(from, to, " ");

      if (!text.trim()) {
        setSelection(null);
        setAiBtnPos(null);
        setShowAI(false);
        return;
      }

      const selectionObj = window.getSelection();
      if (!selectionObj || selectionObj.rangeCount === 0) return;

      const range = selectionObj.getRangeAt(0);
      const rect = range.getBoundingClientRect();

      setSelection({ from, to, text });

      setAiBtnPos({
        top: rect.top,
        left: rect.left, //  left start of selected text
      });
    };

    editor.on("selectionUpdate", updateSelection);

    return () => {
      editor.off("selectionUpdate", updateSelection);
    };
  }, [editor]);

  /* ───────── REPLACE SELECTED TEXT ───────── */
  const handleReplace = (text: string) => {
    if (!editor || !selection) return;

    editor
      .chain()
      .focus()
      .insertContentAt({ from: selection.from, to: selection.to }, text)
      .run();

    setShowAI(false);
    setSelection(null);
    setAiBtnPos(null);
  };
  const handlePageReplace = (updatedPage: Page) => {
    if (!editor) return;

    editor.commands.setContent(updatedPage.body);

    setShowPageAI(false);
  };

  return (
    <>
      {/* ───── TOOLBAR ───── */}
      <div
        className="editor-toolbar no-print"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "20px",
        }}
      >
        <Button
          size="small"
          onClick={() => setShowPageAI(true)}
          sx={{
            minWidth: 0,
            width: 40,
            height: 40,
            borderRadius: "50%",
            backgroundColor: {
              xs: "transparent !important",
              sm: "#4F46E5 !important",
            },
            p: 0,
            color: {
              xs: "#4F46E5",
              sm: "#fff",
            },

            boxShadow: {
              xs: "none",
              sm: undefined,
            },
          }}
        >
          <AutoFixHighIcon fontSize="small" />
        </Button>

        <WordToolbar editor={editor} />
      </div>

      {/* ───── EDITOR ───── */}
      <Box
        sx={{
          "& .ProseMirror": {
            background: documentTheme.background,
            color: documentTheme.bodyColor,
            fontFamily: documentTheme.bodyFont,
            padding: "32px",
            minHeight: "100%",
          },

          "& .ProseMirror h1": {
            fontSize: "30px",
            fontWeight: 700,
            color: documentTheme.titleColor,
            fontFamily: documentTheme.titleFont,
            position: "relative",
            marginBottom: "20px",
          },

          ...(documentTheme.headingStyle === "underline" && {
            "& .ProseMirror h1": {
              borderBottom: `2px solid ${documentTheme.titleColor}`,
              paddingBottom: "6px",
            },
          }),
          ...(documentTheme.headingStyle === "left-accent" && {
            "& .ProseMirror h1": {
              paddingLeft: "18px",
              position: "relative",
              color: documentTheme.titleColor,
            },

            /* h1 accent */
            "& .ProseMirror h1::before": {
              content: '""',
              position: "absolute",
              left: 0,
              top: "0.2em",
              height: "40px",
              width: "3px", // thicker
              backgroundColor: documentTheme.titleColor,
              borderRadius: "3px",
            },
          }),

          ...(documentTheme.headingStyle === "center-line" && {
            "& .ProseMirror h1": {
              textAlign: "center",
              color: documentTheme.titleColor,
            },
            "& .ProseMirror h1::after": {
              content: '""',
              display: "block",
              width: "60px",
              height: "2px",
              backgroundColor: documentTheme.titleColor,
              margin: "12px auto 0",
            },
          }),
        }}
      >
        <EditorContent editor={editor} />
      </Box>

      {/* ───── CHAT WITH AI BUTTON ───── */}
      {selection && aiBtnPos && !showAI && (
        <button
          className="ai-float-btn"
          style={{
            position: "fixed",
            top: aiBtnPos.top - 6, // small gap above text
            left: aiBtnPos.left,
            transform: "translateY(-100%)", // only move up
          }}
          onClick={() => setShowAI(true)}
        >
          Chat with AI
        </button>
      )}

      {/* ───── AI CHAT BOX ───── */}
      {showAI && selection && (
        <InternalAIChatBox
          selection={selection}
          onReplace={(text) => {
            handleReplace(text); //  USE THIS

            setShowAI(false);
            setSelection(null); //  IMPORTANT
          }}
          onClose={() => {
            setShowAI(false);
            setSelection(null); //  IMPORTANT
          }}
        />
      )}
      {showPageAI && editor && (
        <PageAIChatBox
          page={{
            id: "page-1", // static id (since this editor handles one page only)
            body: editor.getHTML(),
          }}
          onReplace={handlePageReplace}
          onClose={() => setShowPageAI(false)}
        />
      )}
    </>
  );
}
